import { Close, Email } from '@mui/icons-material';
import { Box, Button, Grid, MenuItem, TextField, Typography } from '@mui/material';
import { deepEqual } from 'fast-equals';
import { Form, Formik } from 'formik';
import { useSnackbar } from 'notistack';
import { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import * as Yup from 'yup';
import { Loader, Page, PageTitleWithBack, SMBSelect } from '../../../components';
import { VendorEmailsTable } from './VendorEmailsTable';
import { createNewACHNotification, getPendingACHNotifications } from '../../../fetch';
import { IPendingNotifications, IACHVendorEmail } from '../../../models';
import { formatMoney, isEmail, formatDate } from '../../../helpers';

const NewNotificationPage = Yup.object().shape({
  batch: Yup.string(),
  to: Yup.string(),
  emailSubject: Yup.string().required('Required'),
  emailBody: Yup.string().required('Required')
});

export const ACHNewNotificationPage = () => {
  const [isLoadingPendingVendors, setIsLoadingPendingVendors] = useState<boolean>(false);
  const [pendingACHNotifications, setPendingACHNotifications] = useState<IPendingNotifications[]>(
    []
  );
  const [vendorEmails, setVendorEmails] = useState<IACHVendorEmail[]>([]);

  const { enqueueSnackbar } = useSnackbar();
  const history = useHistory();

  const fetchPendingNotifications = async () => {
    try {
      setIsLoadingPendingVendors(true);
      const res = await getPendingACHNotifications();
      setPendingACHNotifications(res.records);
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoadingPendingVendors(false);
    }
  };

  useEffect(() => {
    fetchPendingNotifications();
  }, []);

  const hasValidVendorEmails = vendorEmails.filter(v => isEmail(v.vendorAchEmail));
  const hasFilledOutAllEmails =
    hasValidVendorEmails?.map(v => v.vendorAchEmail)?.filter(Boolean)?.length ===
    vendorEmails?.length;

  return (
    <Page title="ACH - New Notification">
      <PageTitleWithBack
        subTitle="Please select a pending ACH batch notification"
        backText="Back to ACH"
        backUrl={'/admin/financial/ach'}
        title="ACH - New Notification"
      />
      <Formik
        enableReinitialize={true}
        initialValues={{
          batch: '',
          to: '',
          emailSubject: 'ACH Payment from Stone Martin Builders dated @@ShortDate@@ for @@VendorName@@',
          emailBody: `Your payment in the amount of @@Amount@@ has been processed as of @@ShortDate@@ and should be deposited into your account within 48 hours of this notice. Your payment details are attached.   If you need further details about this payment or any others you have received, please login to you BuilderLinq portal at @@WebAddress@@.    If you have any other questions or issues, you may contact billing@stonemartin.com.`
        }}
        validationSchema={NewNotificationPage}
        onSubmit={async values => {
          try {
            const res = await createNewACHNotification({
              batchNumber: Number(values.batch),
              emailSubject: values.emailSubject,
              emailBody: values.emailBody,
              vendorEmails:
                vendorEmails?.length > 0
                  ? vendorEmails.map(v => ({
                      email: v.vendorAchEmail,
                      vendorSageId: v.vendorSageId
                    }))
                  : []
            });
            if (res?.Detail) {
              return enqueueSnackbar(`Error, ${res.Detail}`, {
                variant: 'error'
              });
            }
            enqueueSnackbar(`New Notification created!`, {
              variant: 'success'
            });
            history.push('/admin/financial/ach');
          } catch (error) {
            enqueueSnackbar(`Error creating a new Notification, please try again.`, {
              variant: 'error'
            });
          }
        }}
      >
        {({
          isSubmitting,
          values,
          initialValues,
          setFieldValue,
          handleSubmit,
          dirty,
          isValid,
          handleBlur,
          errors,
          touched
        }) => {
          return (
            <Form onSubmit={handleSubmit} autoComplete="none">
              {isSubmitting && <Loader type="overlay" position="centered" title="Sending..." />}
              <Grid container spacing={1}>
                <Grid item xs={12} lg={6}>
                  <Grid container spacing={1}>
                    <Grid item xs={12}>
                      <SMBSelect
                        disabled={isLoadingPendingVendors || pendingACHNotifications.length < 1}
                        variant="outlined"
                        autoComplete="nope"
                        label={isLoadingPendingVendors ? 'Loading...' : 'ACH Batch'}
                        name="batch"
                        value={values.batch}
                        onBlur={handleBlur}
                        size="small"
                        handleClear={() => {
                          setFieldValue('batch', '');
                          setFieldValue('to', '');
                        }}
                        onChange={e => {
                          if (e.target.value === null) {
                            setFieldValue('batch', '');
                            setFieldValue('to', '');
                          } else {
                            setFieldValue('batch', e.target.value);
                            setFieldValue(
                              'to',
                              pendingACHNotifications
                                .filter(
                                  pendingNotification =>
                                    pendingNotification.batch === +e.target.value
                                )[0]
                                .vendors.map(vendor => vendor.vendor)
                                .join(', ')
                            );
                          }
                        }}
                      >
                        <MenuItem key={`0`} value={null}>
                          Not selected
                        </MenuItem>
                        {pendingACHNotifications.length > 0 &&
                          pendingACHNotifications.map((note: IPendingNotifications, index) => {
                            return (
                              <MenuItem key={`${index}`} value={note.batch}>
                                ACH Batch {note?.batch ?? 0} (created {formatDate(note.batchDate)})
                                - {note.vendors?.length} vendor{note.vendors?.length > 1 ? 's' : ''}
                                , {note?.totalNumberOfPayments ?? 0} payment
                                {note?.totalNumberOfPayments > 1 ? 's' : ''},{' '}
                                {formatMoney(note.totalValueOfPayments)} value
                              </MenuItem>
                            );
                          })}
                      </SMBSelect>
                    </Grid>
                    {values.batch && (
                      <>
                        <Grid item xs={12}>
                          <TextField
                            fullWidth
                            required
                            InputProps={{
                              readOnly: true
                            }}
                            variant="outlined"
                            autoComplete="nope"
                            label="To"
                            name="to"
                            value={values.to}
                            onBlur={handleBlur}
                            size="small"
                            onChange={e => setFieldValue('to', e.target.value)}
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <TextField
                            fullWidth
                            required
                            variant="outlined"
                            autoComplete="nope"
                            label="Email Subject"
                            name="emailSubject"
                            value={values.emailSubject}
                            onBlur={handleBlur}
                            size="small"
                            onChange={e => setFieldValue('emailSubject', e.target.value)}
                            error={Boolean(touched.emailSubject && errors.emailSubject)}
                            helperText={touched.emailSubject && errors.emailSubject}
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <TextField
                            fullWidth
                            multiline
                            rows={5}
                            required
                            variant="outlined"
                            autoComplete="nope"
                            label="Email Body"
                            name="emailBody"
                            value={values.emailBody}
                            onBlur={handleBlur}
                            size="small"
                            onChange={e => setFieldValue('emailBody', e.target.value)}
                            error={Boolean(touched.emailBody && errors.emailBody)}
                            helperText={touched.emailBody && errors.emailBody}
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <Typography variant="caption">
                            The text you see between @@ symbols {'@@xxxxx@@'} are holding places for
                            dynamic values that BuilderLinq will fill in for each email before
                            sending. Please do not attempt to remove or edit this text in the subject or body
                            so as not to compromise this functionality.
                          </Typography>
                        </Grid>
                      </>
                    )}
                  </Grid>
                </Grid>
                {values.batch && (
                  <Grid item xs={12} lg={6}>
                    <VendorEmailsTable
                      batchNumber={values.batch}
                      setVendorEmails={setVendorEmails}
                      vendorEmails={vendorEmails}
                    />
                  </Grid>
                )}
              </Grid>
              <Box marginTop="1rem">
                <Button
                  disabled={!dirty || isSubmitting || !isValid || !hasFilledOutAllEmails}
                  type="submit"
                  startIcon={<Email />}
                  variant="contained"
                  color="primary"
                >
                  Send
                </Button>
                <Button
                  type="button"
                  variant="contained"
                  color="inherit"
                  disabled={isSubmitting}
                  startIcon={<Close />}
                  onClick={() => {
                    if (!deepEqual(initialValues, values)) {
                      const result = window.confirm(
                        'You have unsaved changes, are you sure you want to exit?'
                      );
                      if (result) {
                        history.push('/admin/financial/ach');
                      } else {
                        return;
                      }
                    } else {
                      history.push('/admin/financial/ach');
                    }
                  }}
                >
                  Cancel
                </Button>
              </Box>
            </Form>
          );
        }}
      </Formik>
    </Page>
  );
};
